import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardNav from "@/components/DashboardNav";
import { api, imageUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, Mail, Phone, Copy, Check, ExternalLink, Zap, ShieldCheck } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { toast } from "sonner";

const STATUS_OPTIONS = ["new", "contacted", "booked", "archived"];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openLead, setOpenLead] = useState(null);
  const [copied, setCopied] = useState(false);

  const publicUrl = `${window.location.origin}/preview/${user?.clinic_slug || ""}`;

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.get("/dashboard/stats"), api.get("/leads")]);
      setStats(s.data);
      setLeads(l.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    const cancelPending = (l) => {
      if ((status === "booked" || status === "archived") && Array.isArray(l.followups)) {
        return l.followups.map((f) => (f.status === "scheduled" ? { ...f, status: "cancelled" } : f));
      }
      return l.followups;
    };
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, status, followups: cancelPending(l) } : l)));
      if (openLead?.id === id) setOpenLead((prev) => ({ ...prev, status, followups: cancelPending(prev) }));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-bone-100">
      <DashboardNav />
      <main className="max-w-[1600px] mx-auto p-6 md:p-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-2">Clinic Console</div>
            <h1 className="font-heading text-4xl md:text-5xl text-forest-700">
              Good to see you, {user?.clinic_name}.
            </h1>
            <p className="text-sm text-forest-400 mt-2">
              Here's what's happening with your AI smile previews.
            </p>
          </div>
          <div className="bg-white border border-bone-200 p-4 flex items-center gap-3 min-w-[300px]">
            <div className="flex-1 min-w-0">
              <div className="overline">Your patient link</div>
              <div className="text-xs text-forest-700 mt-1 font-mono truncate" data-testid="public-link">
                {publicUrl}
              </div>
            </div>
            <button onClick={copyLink} data-testid="copy-link-btn" className="p-2 border border-bone-200 hover:bg-bone-100" aria-label="Copy">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="open-link-btn" className="p-2 border border-bone-200 hover:bg-bone-100" aria-label="Open">
              <ExternalLink size={16} />
            </a>
          </div>
        </div>

        {/* Plan / Quota Banner */}
        <PlanBanner user={user} previews={stats?.total_previews ?? 0} leads={stats?.unique_leads ?? 0} />

        {/* Setup completion nudge */}
        {user && !user.booking_url && (
          <div className="mb-8 bg-bone-200/60 border border-bone-300 px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="setup-nudge">
            <div>
              <div className="overline">Almost there</div>
              <div className="text-sm text-forest-700 mt-1">
                Add your booking URL so patients can book a consult right after seeing their AI smile.
              </div>
            </div>
            <Link to="/dashboard/settings" className="btn-primary !py-2 !px-4 text-xs" data-testid="finish-setup-btn">
              Finish setup →
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mb-10">
          <StatCard label="Total previews" value={stats?.total_previews ?? "—"} />
          <StatCard label="New leads" value={stats?.new_leads ?? "—"} accent />
          <StatCard label="Contacted" value={stats?.contacted ?? "—"} />
          <StatCard label="Booked" value={stats?.booked ?? "—"} />
          <StatCard label="Conversion" value={stats ? `${stats.conversion_rate}%` : "—"} />
        </div>

        {/* Leads table */}
        <div className="bg-white border border-bone-200">
          <div className="px-6 py-5 border-b border-bone-200 flex items-center justify-between">
            <div>
              <div className="overline">Patient Leads</div>
              <div className="text-sm text-forest-400 mt-1">{leads.length} total</div>
            </div>
            <div className="text-xs text-forest-400">{stats?.this_month ?? 0} this month</div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-forest-400 text-sm">Loading leads…</div>
          ) : leads.length === 0 ? (
            <EmptyState publicUrl={publicUrl} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="text-left text-forest-400 border-b border-bone-200">
                    <th className="px-6 py-3 font-normal overline">Patient</th>
                    <th className="px-6 py-3 font-normal overline">Contact</th>
                    <th className="px-6 py-3 font-normal overline">Submitted</th>
                    <th className="px-6 py-3 font-normal overline">Nurture</th>
                    <th className="px-6 py-3 font-normal overline">Status</th>
                    <th className="px-6 py-3 font-normal overline text-right">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-bone-200 last:border-0 hover:bg-bone-100/40">
                      <td className="px-6 py-4">
                        <div className="font-medium text-forest-700">{l.patient_name}</div>
                      </td>
                      <td className="px-6 py-4 text-forest-400">
                        <div className="flex items-center gap-1 text-xs"><Mail size={12} /> {l.patient_email}</div>
                        {l.patient_phone && (
                          <div className="flex items-center gap-1 text-xs mt-1"><Phone size={12} /> {l.patient_phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-forest-400 text-xs">
                        {new Date(l.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={l.status}
                          data-testid={`status-select-${l.id}`}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          className="border border-bone-200 px-2 py-1 text-xs bg-white"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => setOpenLead(l)}
                          data-testid={`view-preview-${l.id}`}
                          className="inline-flex items-center gap-2 text-forest-600 hover:text-forest-700 text-xs"
                        >
                          <Eye size={14} /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {openLead && (
        <PreviewModal lead={openLead} onClose={() => setOpenLead(null)} onUpdateStatus={updateStatus} />
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`p-6 border ${accent ? "bg-forest-600 text-white border-forest-600" : "bg-white border-bone-200"}`}>
      <div className={`overline ${accent ? "!text-bone-200/70" : ""}`}>{label}</div>
      <div className="font-body font-semibold text-4xl mt-3 tabular-nums tracking-tight">{value}</div>
    </div>
  );
}

function NurtureBadge({ status, count }) {
  if (!status) {
    return <span className="text-[10px] uppercase tracking-[0.18em] text-forest-400/60">—</span>;
  }
  const label =
    status === "scheduled"
      ? `${count || 3}-touch drip active`
      : status === "canceled"
      ? "Stopped after booking"
      : status;
  const cls =
    status === "scheduled"
      ? "bg-forest-600 text-white"
      : status === "canceled"
      ? "bg-bone-200 text-forest-700"
      : "bg-bone-100 text-forest-400";
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-[0.16em] px-2 py-1 ${cls}`}
      data-testid="nurture-badge"
    >
      {label}
    </span>
  );
}

function PlanBanner({ user, previews = 0, leads = 0 }) {
  if (!user) return null;

  const startCheckout = async (plan) => {
    try {
      const { data } = await api.get(`/billing/checkout-url`, { params: { plan } });
      localStorage.setItem("toothpix_pending_plan", plan);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  // ---- Pilot plan ---------------------------------------------------------
  if (user.plan === "pilot") {
    const ends = user.pilot_ends_at ? new Date(user.pilot_ends_at) : null;
    const daysLeft = ends ? Math.max(0, Math.ceil((ends - new Date()) / (1000 * 60 * 60 * 24))) : null;
    const readOnly = !!user.pilot_read_only;
    const endDateStr = ends ? ends.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
    const contactName = user.pilot_contact_name || "your Toothpix contact";
    const contactUrl = user.pilot_contact_url || "";

    if (readOnly) {
      return (
        <div
          className="mb-8 border p-5 md:p-6 flex flex-col md:flex-row gap-4 md:gap-6 md:items-center bg-clay-400/10 border-clay-600/30"
          data-testid="plan-banner"
        >
          <div className="flex-1">
            <div className="overline !text-clay-600">Pilot ended</div>
            <div className="mt-2 font-heading text-2xl text-forest-700">Your pilot has ended.</div>
            <p className="text-sm text-forest-400 mt-2 max-w-xl">
              Your dashboard and past leads stay available, but new smile previews are paused
              (your patient link and website widget are switched off) until you upgrade.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={() => startCheckout("clinic")} className="btn-primary !py-2.5 !px-5 text-sm" data-testid="upgrade-clinic">
              Upgrade to Clinic · $499
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className="mb-8 border p-5 md:p-6 bg-forest-600 text-white border-forest-600"
        data-testid="plan-banner"
      >
        <div className="overline !text-bone-200/70 flex items-center gap-1"><Zap size={12} /> Pilot</div>
        <div className="mt-2 font-body text-lg md:text-xl" data-testid="pilot-summary">
          <span className="font-semibold">{daysLeft} days left</span>
          <span className="text-bone-100/80"> · {previews} previews so far · {leads} leads</span>
        </div>
        {daysLeft !== null && daysLeft <= 5 && (
          <div className="mt-3 text-sm text-bone-100/90" data-testid="pilot-endgame">
            Your pilot ends {endDateStr}. Talk to{" "}
            {contactUrl ? (
              <a href={contactUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2 text-white font-medium" data-testid="pilot-contact-link">
                {contactName}
              </a>
            ) : (
              <span className="text-white font-medium">{contactName}</span>
            )}{" "}
            about keeping it running.
          </div>
        )}
      </div>
    );
  }

  // ---- Trial / paid plans -------------------------------------------------
  const used = user.transformations_used || 0;
  const quota = user.transformations_quota;
  const isUnlimited = quota === -1;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, quota)) * 100));
  const isTrial = user.plan === "trial";
  const trialEnds = user.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const daysLeft = trialEnds ? Math.max(0, Math.ceil((trialEnds - new Date()) / (1000 * 60 * 60 * 24))) : null;
  const planLabel = { trial: "Free Trial", clinic: "Clinic · $499/mo", group: "Group", pilot: "Pilot" }[user.plan] || user.plan;

  return (
    <div
      className={`mb-8 border p-5 md:p-6 flex flex-col md:flex-row gap-4 md:gap-6 md:items-center ${
        isTrial ? "bg-forest-600 text-white border-forest-600" : "bg-white border-bone-200"
      }`}
      data-testid="plan-banner"
    >
      <div className="flex-1">
        <div className={`overline ${isTrial ? "!text-bone-200/70" : ""}`}>{planLabel}</div>
        <div className="mt-2 flex items-baseline gap-3">
          <div className="font-body font-semibold text-2xl tabular-nums">
            {used}
            <span className={`text-sm font-normal ml-1 ${isTrial ? "text-bone-100/70" : "text-forest-400"}`}>
              / {isUnlimited ? "∞" : quota} smile transforms this period
            </span>
          </div>
        </div>
        {!isUnlimited && (
          <div className={`mt-2 h-1.5 rounded-none ${isTrial ? "bg-white/20" : "bg-bone-200"}`}>
            <div
              className={`h-full ${isTrial ? "bg-white" : "bg-forest-600"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        {isTrial && daysLeft !== null && (
          <div className="mt-2 text-xs text-bone-100/80 flex items-center gap-1">
            <Zap size={12} /> {daysLeft} days left in trial
          </div>
        )}
        {!isTrial && !user.payment_verified && (
          <div className="mt-2 text-xs text-forest-400 flex items-center gap-1">
            <ShieldCheck size={12} /> Payment marked as received. Awaiting verification by Toothpix HQ.
          </div>
        )}
      </div>
      {isTrial ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => startCheckout("clinic")}
            className="px-5 py-2.5 bg-white text-forest-600 hover:bg-bone-100 text-sm font-medium"
            data-testid="upgrade-clinic"
          >
            Upgrade to Clinic · $499
          </button>
        </div>
      ) : (
        <Link
          to="/dashboard/settings"
          className="btn-secondary !py-2 !px-5"
          data-testid="manage-account"
        >
          Manage account
        </Link>
      )}
    </div>
  );
}

function EmptyState({ publicUrl }) {
  return (
    <div className="px-6 py-20 text-center">
      <div className="overline mb-4">No leads yet</div>
      <h3 className="font-heading text-3xl text-forest-700">
        Share your patient link to receive your first lead.
      </h3>
      <p className="text-sm text-forest-400 mt-3 max-w-md mx-auto">
        Add it to your Instagram bio, Google Business profile, or SMS it to
        recent inquiries.
      </p>
      <div className="inline-block mt-8 px-4 py-3 bg-bone-100 border border-bone-200 font-mono text-xs text-forest-700">
        {publicUrl}
      </div>
    </div>
  );
}

function FollowupStatus({ status }) {
  const map = {
    sent: { label: "Sent", cls: "bg-forest-600 text-white" },
    scheduled: { label: "Scheduled", cls: "bg-bone-100 text-forest-500 border border-bone-200" },
    cancelled: { label: "Cancelled", cls: "bg-clay-400/15 text-clay-600" },
  };
  const s = map[status] || map.scheduled;
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 ${s.cls}`} data-testid={`followup-status-${status}`}>
      {s.label}
    </span>
  );
}

function PreviewModal({ lead, onClose, onUpdateStatus }) {
  return (
    <div className="fixed inset-0 z-40 bg-forest-700/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white max-w-4xl w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="preview-modal"
      >
        <div className="px-6 py-5 border-b border-bone-200 flex items-start justify-between">
          <div>
            <div className="overline">Smile Preview</div>
            <div className="font-heading text-2xl mt-1 text-forest-700">{lead.patient_name}</div>
            <div className="text-xs text-forest-400 mt-1">
              {lead.patient_email} {lead.patient_phone && `· ${lead.patient_phone}`}
            </div>
          </div>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 text-2xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="p-6">
          <BeforeAfterSlider beforeSrc={imageUrl(lead.before_url)} afterSrc={imageUrl(lead.after_url)} aspect="aspect-[4/3]" />
          <div className="mt-6 flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => onUpdateStatus(lead.id, s)}
                className={`px-4 py-2 text-xs border transition-colors ${
                  lead.status === s
                    ? "bg-forest-600 text-white border-forest-600"
                    : "border-bone-200 text-forest-400 hover:text-forest-700"
                }`}
                data-testid={`modal-status-${s}`}
              >
                Mark as {s}
              </button>
            ))}
          </div>

          {/* Follow-up sequence */}
          <div className="mt-8" data-testid="followup-timeline">
            <div className="overline">Automatic follow-ups</div>
            {(lead.followups && lead.followups.length > 0) ? (
              <div className="mt-3 divide-y divide-bone-200 border border-bone-200">
                {lead.followups.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3" data-testid={`followup-${i}`}>
                    <div>
                      <div className="text-sm text-forest-700">{f.label}</div>
                      <div className="text-xs text-forest-400 mt-0.5">
                        {f.scheduled_at ? new Date(f.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                      </div>
                    </div>
                    <FollowupStatus status={f.status} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-forest-400 mt-2">No automatic follow-ups were scheduled for this lead.</p>
            )}
            {(lead.status === "booked" || lead.status === "archived") && (
              <p className="text-xs text-forest-400 mt-3">
                Remaining follow-ups were cancelled when this lead was marked {lead.status}.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
